<template>
<!-- eslint-disable max-len -->
<div class="container">
<div class="row no-gutters">
<div class="login-form col-sm-12">

  <h2>Decision Data Report Generator</h2>
  <div style="font-size: 75%; margin-bottom: 10px;">
    If not all playthroughs are marked complete, note that recent progress may not show up in this data until later, as the data takes time to process.
  </div>

  <select-input
    label
    class="col-sm-6"
    v-model="selectedConfigName"
    @input="onConfigSelected"
    :options="configs"
    use="name"
    :display="g => { return `${g.name} (${(new Date(g.updated)).toLocaleString()})`; }"
    :remove="selectedConfigName ? deleteConfig : null"
  >
    Saved Report Configuration
  </select-input>
  <b-input-group style="left: 15px;">
    <text-input
      label
      v-model="configNameToSave"
    >
      Report Configuration Save Name
    </text-input>
    <b-input-group-append>
      <button
        class="btn"
        style="height: 40%; margin-top: 33px;"
        :disabled="
          !((classesToInclude.length && classesToInclude[0] && classesToInclude[0]._id)
          || (gamesToInclude.length && gamesToInclude[0] && gamesToInclude[0]._id)
          || (requireClass && canManageAllReportConfigurations))
        "
        @click.prevent="saveConfig"
      >
        Save Report Configuration
      </button>
    </b-input-group-append>
  </b-input-group>
  <div style="margin-bottom: 15px; height: 3px; width: 100%; background-color: black;"/>

  <form>
    <array-input
      v-if="games.length > 0 && (canManageAllReportConfigurations || !requireClass)"
      :items="gamesToInclude"
      :hide-button="gamesToInclude.length >= games.length"
    >
      <h5 slot="title">Included Gamulations</h5>

      <div class="form-row" slot="item" slot-scope="{ item: includedGame, index: i }">
        <select-input
          class="col-sm-6"
          v-model="includedGame._id"
          :options="unused(games).by('_id').in(gamesToInclude).by('_id').at(i)"
          use="_id"
          :display="g => { return `${g.name}`; }"
          required
          autofocus
          :to="(id) => { return `/blade/games/${id}/edit`; }"
          :remove="() => { gamesToInclude.splice(i, 1); updateMetrics(); }"
          @change="updateMetrics"
        >
          Gamulation
        </select-input>
      </div>
      <span slot="button">Include Gamulation</span>
    </array-input>

    <array-input
      :items="classesToInclude"
      :hide-button="classesToInclude.length >= classes.length"
    >
      <h5 slot="title">Included {{classTypeNamePlural}}</h5>

      <div class="form-row" slot="item" slot-scope="{ item: includedClass, index: i }">
        <select-input
          class="col-sm-6"
          v-model="includedClass._id"
          :options="unused(classes).by('_id').in(classesToInclude).by('_id').at(i)"
          use="_id"
          :display="c => { return `${c.className}`; }"
          required
          autofocus
          :to="(id) => { return `/blade/classes/${id}/edit`; }"
          :remove="() => { classesToInclude.splice(i, 1); updateMetrics(); }"
          @change="updateMetrics"
        >
          {{classTypeName}}
        </select-input>
      </div>
      <span slot="button">Include {{classTypeName}}</span>
    </array-input>

    <template
      v-if="
        (classesToInclude.length && classesToInclude[0] && classesToInclude[0]._id)
        || (gamesToInclude.length && gamesToInclude[0] && gamesToInclude[0]._id)
        || canManageAllReportConfigurations
      "
    >
      <checkbox-input v-model="requireClass">Require {{classTypeName}}</checkbox-input>
      <checkbox-input v-model="includeEmail">Include Email</checkbox-input>
      <checkbox-input v-model="includeName">Include Name</checkbox-input>
      <checkbox-input v-model="onlyFeedback">Only Feedback</checkbox-input>
      <checkbox-input v-model="includeCompletedGame">Include Completed Gamulation</checkbox-input>
      <checkbox-input v-model="excludeFeedback">Exclude Feedback</checkbox-input>
      <checkbox-input v-model="includeDuration">Include Duration</checkbox-input>
      <checkbox-input v-model="excludeReason">Exclude Reason (free responses show in choice column)</checkbox-input>
      <checkbox-input v-model="oneRowPerGameUser">One Row Per Game User</checkbox-input>
      <checkbox-input
        v-if="oneRowPerGameUser"
        v-model="useReportTitlePrefix"
      >Use Decision Report Title As Column Header Prefixes (removes Decision columns)</checkbox-input>
      <checkbox-input v-model="includeChangedMetrics">Include Decision Metrics</checkbox-input>
      <checkbox-input
        v-if="includeChangedMetrics"
        v-model="useMetricDeltas"
      >Display Change In Decision Metric Values (instead of the literal value of the Metric after the decision is made)</checkbox-input>
      <array-input
        v-if="includeChangedMetrics"
        :items="metricsToInclude"
        :hide-button="metricsToInclude.length >= metrics.length"
      >
        <h5 slot="title">Only Output These Decision Metrics (leave empty to include all)</h5>

        <div class="form-row" slot="item" slot-scope="{ item: decisionMetric, index: i }">
          <select-input
            class="col-sm-6"
            v-model="decisionMetric._id"
            :options="unused(metrics).by('_id').in(metricsToInclude).by('_id').at(i)"
            use="_id"
            :display="m => { return `${m.name} (${m.type})`; }"
            required
            autofocus
            :to="(id) => { return `/blade/metrics/${id}/edit`; }"
            :remove="() => { metricsToInclude.splice(i, 1); }"
          >
            Metric
          </select-input>
        </div>
        <span slot="button">Add Decision Metric</span>
      </array-input>

      <array-input
        :items="demographicMetrics"
        :hide-button="demographicMetrics.length >= metrics.length"
      >
        <h5 slot="title">Demographic Metrics</h5>

        <div class="form-row" slot="item" slot-scope="{ item: demographicMetric, index: i }">
          <select-input
            class="col-sm-6"
            v-model="demographicMetric._id"
            :options="unused(metrics).by('_id').in(demographicMetrics).by('_id').at(i)"
            use="_id"
            :display="m => { return `${m.name} (${m.type})`; }"
            required
            autofocus
            :to="(id) => { return `/blade/metrics/${id}/edit`; }"
            :remove="() => { demographicMetrics.splice(i, 1); }"
          >
            Metric
          </select-input>
        </div>
        <span slot="button">Add Demographic Metric</span>
      </array-input>

      <checkbox-input v-model="includeAllUsers">
        Include All Users (don't limit to {{classTypeNameLower}} students/employees or users with the gamulation assigned)
      </checkbox-input>
    </template>

    <button
      type="submit"
      class="btn"
      @click.prevent="onSubmit"
      :disabled="
        submitted
        || !((classesToInclude.length && classesToInclude[0] && classesToInclude[0]._id)
          || (gamesToInclude.length && gamesToInclude[0] && gamesToInclude[0]._id))
      "
    >
      <span style="margin: 0 3px;">Download Decision Data</span>
    </button>
  </form>
  <router-link
    :to="`/blade/usageData`"
    class="btn"
    style="
      margin-top: 12px;
      padding: 5px;
      position: absolute;
      top: 0;
      right: 12px;
    "
  >
    <span style="margin: 0 3px;">Usage Data</span>
  </router-link>
</div>
</div>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FileSaver from 'file-saver';

import ArrayInput from '../../ui/inputs/ArrayInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';

import { unused } from '../../../utils/utils';
import { objectToCSV } from '../../../utils/csv-util';

export default {
  components: {
    ArrayInput,
    CheckboxInput,
    TextInput,
    SelectInput
  },
  data() {
    return {
      includeEmail: false,
      includeName: false,
      onlyFeedback: false,
      includeCompletedGame: false,
      excludeFeedback: false,
      includeAllUsers: false,
      includeDuration: false,
      oneRowPerGameUser: false,
      useReportTitlePrefix: false,
      includeChangedMetrics: false,
      useMetricDeltas: false,
      excludeReason: false,
      demographicMetrics: [],
      metricsToInclude: [],
      gamesToInclude: [],
      classesToInclude: [],
      submitted: false,
      metrics: [],
      games: [],
      classes: [],
      requireClass: false,
      configs: [],
      selectedConfigName: '',
      configNameToSave: '',
      unused
    };
  },
  computed: {
    ...mapGetters([
      'canSelectGame',
      'classTypeName',
      'classTypeNamePlural',
      'classTypeNameLower',
      'canManageAllReportConfigurations',
    ])
  },
  async created() {
    await this.updateMetrics();
    try {
      this.games = ((this.canSelectGame) ? (await this.getGames()) : await this.getAssignedGames());
      this.classes = await this.getClasses();
      this.configs = await this.getReasonDataConfigs();
    } catch (err) {
      const message = 'Error getting gamulations/classes/report configurations, please refresh';
      this.flashDanger({ message });
    }
  },
  methods: {
    ...mapActions([
      'getReasonData',
      'getReasonDataConfigs',
      'getReasonDataConfig',
      'createReasonDataConfig',
      'deleteReasonDataConfig',
      'getAssignedMetrics',
      'getClasses',
      'getGames',
      'getAssignedGames',
      'flashPrimary',
      'flashSuccess',
      'flashDanger'
    ]),
    async updateMetrics() {
      try {
        this.metrics = await this.getAssignedMetrics({
          gamesToInclude: !(this.gamesToInclude) ? [] : this.gamesToInclude.filter(g => g && g._id),
          classesToInclude: !(this.classesToInclude) ? [] : this.classesToInclude.filter(c => c && c._id),
        });
      } catch (err) {
        const message = 'Error getting metrics, please refresh';
        this.flashDanger({ message });
      }
    },
    async onSubmit() {
      if (this.submitted) return;
      this.submitted = true;

      try {
        this.flashPrimary({ message: 'Please wait. Decision data downloading...' });

        const dataObject = await this.getReasonData({
          includeEmail: this.includeEmail,
          includeName: this.includeName,
          gamesToInclude: this.gamesToInclude,
          classesToInclude: this.classesToInclude,
          includeAllUsers: this.includeAllUsers,
          onlyFeedback: this.onlyFeedback,
          includeCompletedGame: this.includeCompletedGame,
          excludeFeedback: this.excludeFeedback,
          includeDuration: this.includeDuration,
          oneRowPerGameUser: this.oneRowPerGameUser,
          useReportTitlePrefix: this.useReportTitlePrefix,
          includeChangedMetrics: this.includeChangedMetrics,
          useMetricDeltas: this.useMetricDeltas,
          excludeReason: this.excludeReason,
          demographicMetrics: this.demographicMetrics,
          metricsToInclude: this.metricsToInclude,
        });

        this.flashSuccess({ message: 'Decision data exporting to CSV...' });

        // console.log(dataObject);

        const csvString = objectToCSV(dataObject);

        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
        FileSaver.saveAs(blob, 'DecisionData.csv');

        this.$nextTick(() => { this.submitted = false; }); // lets you download again if you want
      } catch (_) {
        const message = 'Error downloading decision data, please try again';
        this.flashDanger({ message });
        this.submitted = false;
      }
    },
    async saveConfig() {
      const savedConfig = await this.createReasonDataConfig({
        name: this.configNameToSave,
        includeEmail: this.includeEmail,
        includeName: this.includeName,
        gamesToInclude: this.gamesToInclude,
        classesToInclude: this.classesToInclude,
        includeAllUsers: this.includeAllUsers,
        onlyFeedback: this.onlyFeedback,
        includeCompletedGame: this.includeCompletedGame,
        excludeFeedback: this.excludeFeedback,
        includeDuration: this.includeDuration,
        oneRowPerGameUser: this.oneRowPerGameUser,
        useReportTitlePrefix: this.useReportTitlePrefix,
        includeChangedMetrics: this.includeChangedMetrics,
        useMetricDeltas: this.useMetricDeltas,
        excludeReason: this.excludeReason,
        demographicMetrics: this.demographicMetrics,
        metricsToInclude: this.metricsToInclude,
      });
      const index = this.configs.findIndex(config => config.name === savedConfig.name);
      if (index > -1) {
        this.$set(this.configs, index, savedConfig);
      } else {
        this.configs.push(savedConfig);
      }
    },
    async onConfigSelected() {
      if (this.selectedConfigName) {
        const config = await this.getReasonDataConfig({ configName: this.selectedConfigName });
        this.configNameToSave = config.name;
        this.includeEmail = config.includeEmail;
        this.includeName = config.includeName;
        this.gamesToInclude = config.gamesToInclude;
        this.classesToInclude = config.classesToInclude;
        this.includeAllUsers = config.includeAllUsers;
        this.onlyFeedback = config.onlyFeedback;
        this.includeCompletedGame = config.includeCompletedGame;
        this.excludeFeedback = config.excludeFeedback;
        this.includeDuration = config.includeDuration;
        this.oneRowPerGameUser = config.oneRowPerGameUser;
        this.useReportTitlePrefix = config.useReportTitlePrefix;
        this.includeChangedMetrics = config.includeChangedMetrics;
        this.useMetricDeltas = config.useMetricDeltas;
        this.excludeReason = config.excludeReason;
        this.demographicMetrics = config.demographicMetrics || [];
        await this.updateMetrics();
        this.metricsToInclude = config.metricsToInclude || [];
      }
    },
    async deleteConfig() {
      if (this.selectedConfigName) {
        // eslint-disable-next-line no-alert
        if (confirm('Permanently delete the selected report configuration?')) {
          await this.deleteReasonDataConfig({ configName: this.selectedConfigName });
          this.selectedConfigName = '';
          try {
            this.configs = await this.getReasonDataConfigs();
          } catch (err) {
            const message = 'Error getting report configurations, please refresh';
            this.flashDanger({ message });
          }
        }
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.login-form {
  background-color: white;
  margin-top: 50px;
  padding: 20px;
  position: relative;
}
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/ReasonDataRequest.vue